// Fallback config, used while GET /v1/onboarding/config loads or if it fails.
// It has EXACTLY the same shape as the API's "Data" object, so the screens
// only ever read one shape, whether the data is real or fallback.
export const DEFAULT_ONBOARDING_CONFIG = {
  language: 'en',
  currency: 'EUR',
  totalSteps: 4,
  welcome: {
    unit: 'L',
    otherStationsPrice: 1.95,
    pompiBestPrice: 1.65,
    verifiedAt: null,
    savingsPerFillUp: 15,
    stats: {rating: 4.9, drivers: 120000},
  },
  fuelFrequency: {
    options: [
      {
        id: 'monthly_1',
        tag: 'CASUAL DRIVER',
        label: '1x a Month',
        highlighted: false,
        default: false,
        yearlySavings: 70,
        forecast: {min: 70, max: 140},
      },
      {
        id: 'monthly_2_3',
        tag: 'MOST POPULAR',
        label: '2–3x a Month',
        highlighted: true,
        default: true,
        yearlySavings: 180,
        forecast: {min: 180, max: 340},
      },
      {
        id: 'weekly',
        tag: 'DAILY COMMUTER',
        label: 'Every Week',
        highlighted: false,
        default: false,
        yearlySavings: 290,
        forecast: {min: 290, max: 520},
      },
      {
        id: 'multiple_weekly',
        tag: 'HEAVY DRIVER',
        label: 'Multiple times / week',
        highlighted: false,
        default: false,
        yearlySavings: 450,
        forecast: {min: 450, max: 800},
      },
    ],
  },
  fuelType: {
    options: [
      {
        id: 'benzine',
        label: 'BENZINE',
        sublabel: 'Petrol / Gas',
        chip: 'E10 / Euro 95',
        default: true,
        saving: {type: 'PER_TANK', value: 22},
      },
      {
        id: 'diesel',
        label: 'DIESEL',
        sublabel: 'Heavy Duty',
        chip: 'B7 Standard',
        default: false,
        saving: {type: 'PER_TANK', value: 26},
      },
      {
        id: 'electric',
        label: 'ELECTRIC',
        sublabel: 'EV & Hybrid',
        chip: 'Plug-in & EV',
        default: false,
        saving: {type: 'PERCENT', value: 40},
      },
      {
        id: 'lpg',
        label: 'LPG / GAS',
        sublabel: 'Autogas Eco',
        chip: 'Autogas',
        default: false,
        saving: {type: 'PER_TANK', value: 15},
      },
    ],
  },
  location: {
    radiusKm: 5,
    preview: {
      stations: [
        {brand: 'ESSO', price: 1.71, isCheapest: true},
        {brand: 'SHELL', price: 1.92, isCheapest: false},
      ],
      savedPerTank: 10.5,
    },
  },
};

// ---- UI-only things the API does not send (looked up by option id) ----
// icon names from components/onboarding/iconShapes.js
export const FREQUENCY_ICONS = {
  monthly_1: 'car',
  monthly_2_3: 'carFront',
  weekly: 'briefcase',
  multiple_weekly: 'bolt',
};

// Used on the summary screen: "Based on your ___ with Pompi live station routing"
export const FILL_UPS_TEXT = {
  monthly_1: '1 fill-up per month',
  monthly_2_3: '2–3 fill-ups per month',
  weekly: 'weekly fill-ups',
  multiple_weekly: 'multiple fill-ups per week',
};

// TODO(API): NOT in the backend response yet. Ask the backend team to add it
// (e.g. a "summary": {planPrice, trialDays, reminderDay} section).
export const PLAN = {
  price: 2.99, // per month after the trial
  trialDays: 30,
  reminderDay: 28,
};

// ---- Formatters ----
const CURRENCY_SYMBOLS = {EUR: '€', USD: '$', GBP: '£'};
export const currencySymbol = code => CURRENCY_SYMBOLS[code] || code;

// 120000 -> "120k"
export const formatDrivers = n =>
  n >= 1000 ? `${+(n / 1000).toFixed(1)}k` : String(n);

// {type:'PER_TANK', value:22} -> "€22/tank"; {type:'PERCENT', value:40} -> "40% kWh"
export const formatSaving = (saving, symbol) =>
  saving.type === 'PERCENT'
    ? `${saving.value}% kWh`
    : `${symbol}${saving.value}/tank`;

// verifiedAt (ISO date string) -> minutes ago, or null when missing/invalid
export const minutesAgo = iso => {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return Number.isFinite(ms) ? Math.max(0, Math.round(ms / 60000)) : null;
};

// ---- Manual city picker (screen 4 bottom sheet) ----
// Shown before the user types. Typed searches go to Google Places
// (services/city-service.js), limited to CITY_SEARCH_COUNTRIES.
// The first four rows come from the design; "The Hague" and its numbers are
// placeholders. TODO(API): real stationsActive counts from the backend.
export const CITY_LIST_LABEL = 'Netherlands & Border';
export const CITY_SEARCH_COUNTRIES = ['nl', 'be', 'de', 'lu'];

export const POPULAR_CITIES = [
  {id: 'amsterdam', name: 'Amsterdam', region: 'North Holland', stationsActive: 78, latitude: 52.3676, longitude: 4.9041},
  {id: 'rotterdam', name: 'Rotterdam', region: 'South Holland', stationsActive: 62, latitude: 51.9244, longitude: 4.4777},
  {id: 'utrecht', name: 'Utrecht', region: 'Utrecht Province', stationsActive: 39, latitude: 52.0907, longitude: 5.1214},
  {id: 'eindhoven', name: 'Eindhoven', region: 'North Brabant', stationsActive: 31, latitude: 51.4416, longitude: 5.4697},
  {id: 'the-hague', name: 'The Hague', region: 'South Holland', stationsActive: 45, latitude: 52.0705, longitude: 4.3007},
];
